import {
  FunctionComponent,
  RefObject,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

// Main Component props type
type GeneralInfoSectionProps = {
  data: {
    highlights?: string | null;
    overview?: string | null;
    brokerCommission?: number | null;
  };
  containerRef?: RefObject<HTMLDivElement>;
  isSeeker?: boolean;
};

// Main Component
export const GeneralInfoSection: FunctionComponent<GeneralInfoSectionProps> = (
  props
) => {
  const { t } = useTranslation(["common"]);

  // Only show broker commission if it's > 0 (treat 0 as null/empty)
  const hasBrokerCommission = props.data.brokerCommission != null && props.data.brokerCommission > 0;

  // Return HTML component
  return (
    <div className="flex flex-col gap-8 mt-8" ref={props.containerRef}>
      {!props.isSeeker &&
        (hasBrokerCommission || props.data.highlights) && (
          <div className="flex flex-col gap-4">
            <h2 className="text-xl font-bold text-slate-800">{t("common:labels.agentHighlights")}</h2>
            {hasBrokerCommission && (
              <p className="text-base text-slate-700">
                <span className="font-semibold">{t("common:labels.brokerCommission")}:</span>{" "}
                {props.data.brokerCommission}%
              </p>
            )}
            {props.data.highlights && (
              <ExpandableText text={props.data.highlights} />
            )}
          </div>
        )}

      <div className="flex flex-col gap-4">
        <h2 className="text-xl font-bold text-slate-800">{t("common:labels.propertyOverview")}</h2>

        {props.data.overview && <ExpandableText text={props.data.overview} />}
        {!props.data.overview && <p>N/A</p>}
      </div>
    </div>
  );
};

// Usable Component props
type ExpandableTextProps = {
  text: string;
};

// Usable Component
const ExpandableText: FunctionComponent<ExpandableTextProps> = (props) => {
  // Constants definitions
  const [showButton, setShowButton] = useState(false);
  const [showText, setShowText] = useState(false);
  const ref = useRef<HTMLParagraphElement | null>(null);

  // Use effect to set if show button or not
  useEffect(() => {
    if (ref.current && ref.current.scrollHeight > ref.current.clientHeight) {
      setShowButton(true);
    }
  }, []);

  // return HTML component
  return (
    <div className="flex flex-col gap-1">
      <p
        className={`prose prose-slate min-w-full line-clamp-[10] prose-ul:leading-4 prose-ol:leading-4 ${
          showText && "line-clamp-none"
        }`}
        dangerouslySetInnerHTML={{ __html: props.text }}
        ref={ref}
      ></p>

      {showButton && (
        <button
          onClick={() => setShowText(!showText)}
          className="w-max text-sky-600 border h-8 px-4 rounded border-slate-400"
        >
          {showText ? "Show less" : "Show more"}
        </button>
      )}
    </div>
  );
};
